/**
 * CredentialCapability.ts
 *
 * Manual re-probe API wrapper for the
 * `POST /v1/credentials/:credentialId/capability/probe` BE endpoint
 * (Slice 3.1 BE, shipped in PR #861). The endpoint accepts an optional
 * `domainId` in the request body, producing two distinct response shapes:
 *
 * - `probeDomain(token, credentialId, domainId)` — per-domain re-probe.
 *   BE returns a single `DashboardDomainAvailability`. Used by Slice 2.3 FE's
 *   per-domain "Re-check availability" from the dashboard picker info modal.
 *
 * - `probeAllDomains(token, credentialId)` — credential-level re-probe.
 *   BE returns a RAW `Record<domainId, CredentialCapabilityAvailability>` map
 *   covering every dashboard-era domain for the credential's appCode. Used by
 *   Slice 2.4 FE's "Re-check now" affordance on a blocked credential row, and
 *   by Epic 3's "Test connection" action on the admin Credentials surfaces.
 *
 *   Note: the all-domains path does NOT return a `{credentialId,
 *   availabilitySummary}` wrapper — it returns the domain map directly.
 *   Callers are responsible for deriving an aggregate summary when needed.
 *   See `deriveAvailabilitySummaryFromRawMap` in CredentialCapabilityService.ts.
 *
 * Response contract: the endpoint returns RAW structured responses, NOT a
 * `TCSAPIResponse` envelope (no NestJS global interceptor wraps it). The
 * success path returns `resp.data` directly; only the error path uses
 * `formatResponse` for user-safe error string extraction.
 *
 * Dashboard-era only. Never call for workflow-era credential surfaces.
 *
 * No Redux / React imports — pure HTTP wrapper per architecture pattern.
 */

import axios, { AxiosError, AxiosResponse } from 'axios'
import { config } from '@/config'
import { formatResponse } from './helpers/formatResponse'
import type { DashboardDomainAvailability } from '@/types/DashboardDomainRegistry.types'
import type { CredentialCapabilityAvailability } from '@/types/DashboardV1.types'

/**
 * The raw per-domain map returned by `probeAllDomains`.
 * Keys are qualified dashboard-era domain ids (e.g. 'campminder.staff-dashboard').
 * This is the true BE wire shape — NOT a `{credentialId, availabilitySummary}` wrapper.
 */
export type CredentialProbeRawMap = Record<string, CredentialCapabilityAvailability>

export const CredentialCapabilityAPI = {
  /**
   * probeDomain — per-domain re-probe.
   *
   * User-driven path — the BE enforces a 5s timeout on the probe. On timeout
   * the BE returns `{ state: 'unknown', reason: 'cold_cache' }`.
   *
   * @param token - JWT bearer token for the authenticated user.
   * @param credentialId - UUID of the credential to probe.
   * @param domainId - Qualified dashboard-era domain id
   *   (e.g. `'campminder.staff-dashboard'`).
   */
  probeDomain: async (
    token: string,
    credentialId: string,
    domainId: string,
  ): Promise<{
    data: DashboardDomainAvailability | undefined
    err: string | undefined
    status: number | undefined
  }> => {
    try {
      const resp: AxiosResponse<DashboardDomainAvailability> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/credentials/${encodeURIComponent(credentialId)}/capability/probe`,
        headers: { Authorization: `Bearer ${token}` },
        data: { domainId },
      })
      return { data: resp.data, err: undefined, status: resp.status }
    } catch (error) {
      const axiosErr = error as AxiosError
      const formatted = formatResponse<DashboardDomainAvailability>(
        null,
        axiosErr,
      )
      return {
        data: formatted.data,
        err: formatted.err,
        status: axiosErr.response?.status,
      }
    }
  },

  /**
   * probeAllDomains — credential-level re-probe.
   *
   * Returns a RAW `Record<domainId, CredentialCapabilityAvailability>` map —
   * every non-disabled dashboard-era domain for the credential's appCode.
   * Callers derive an aggregate `CredentialAvailabilitySummary` via
   * `deriveAvailabilitySummaryFromRawMap` in CredentialCapabilityService.ts.
   *
   * @param token - JWT bearer token for the authenticated user.
   * @param credentialId - UUID of the credential to probe.
   */
  probeAllDomains: async (
    token: string,
    credentialId: string,
  ): Promise<{
    data: CredentialProbeRawMap | undefined
    err: string | undefined
  }> => {
    try {
      const resp: AxiosResponse<CredentialProbeRawMap> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/credentials/${encodeURIComponent(credentialId)}/capability/probe`,
        headers: { Authorization: `Bearer ${token}` },
      })
      return { data: resp.data, err: undefined }
    } catch (error) {
      return formatResponse<CredentialProbeRawMap>(null, error as AxiosError)
    }
  },
}
